import { Node } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { RawHtmlView } from '../RawHtmlView'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    rawHtml: {
      /** Insert an empty raw-HTML block at the cursor. */
      insertRawHtml: (html?: string) => ReturnType
    }
  }
}

/**
 * A block node that stores arbitrary HTML as a string attribute — the editor's
 * "escape hatch". TipTap never parses or owns the markup; it is rendered
 * verbatim (unescaped) on output and edited via a textarea NodeView.
 *
 * Security: the HTML is sanitized server-side before it is persisted/served.
 */
export const RawHtml = Node.create({
  name: 'rawHtml',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      html: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-html') ?? element.innerHTML,
      },
    }
  },

  parseHTML() {
    return [{ tag: 'div[data-raw-html]' }]
  },

  // Return a real DOM node so getHTML() serializes the inner markup unescaped.
  renderHTML({ node }) {
    const dom = document.createElement('div')
    dom.setAttribute('data-raw-html', '')
    dom.innerHTML = (node.attrs.html as string) || ''
    return dom
  },

  addNodeView() {
    return ReactNodeViewRenderer(RawHtmlView)
  },

  addCommands() {
    return {
      insertRawHtml:
        (html = '') =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs: { html } }),
    }
  },
})
